import { ArrowRight, CheckCircle2, Headphones } from "lucide-react";
import { heroBackground, previewVideo } from "../data/courseData";

export const HeroSection = ({
  onBuy,
  onEnterStudio,
  isPaying = false,
  hasActiveEntitlement = false,
  isEntitlementLoading = false,
}) => {
  const ctaDisabled = isPaying || isEntitlementLoading;

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden flex items-end pb-24 md:pb-32"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={heroBackground}
          alt="Mixing console"
          className="w-full h-full object-cover opacity-70"
        />
        {/* Multi-layer gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_#050505_85%)]" />
      </div>

      {/* Subtle gold light accent */}
      <div
        aria-hidden
        className="absolute top-1/3 right-[-200px] w-[500px] h-[500px] rounded-full opacity-[0.12] blur-[120px]"
        style={{ background: "#C5A059" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full">
        <div className="grid items-end gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(420px,0.72fr)]">
          <div className="max-w-4xl fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-pulse" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300">
              {hasActiveEntitlement
                ? "Studio Access Active"
                : "The Vocal Mixing Masterclass"}
            </span>
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-extrabold leading-[1.02] tracking-tighter text-white">
            Master <span className="gold-text italic font-light">vocal</span>
            <br />
            mixing.
          </h1>

          <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed font-light">
            Learn the exact processing chains, listening habits, and creative decisions
            that platinum-record mix engineers rely on — recorded inside a working studio,
            session by session.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="hero-buy-subscription"
              onClick={hasActiveEntitlement ? onEnterStudio : onBuy}
              disabled={ctaDisabled}
              className="group relative inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C5A059] text-black font-medium text-sm tracking-wide hover:bg-[#E5BE6B] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 shadow-[0_8px_40px_-8px_rgba(197,160,89,0.5)]"
            >
              {hasActiveEntitlement ? (
                <>
                  Enter Studio
                  <Headphones className="w-4 h-4" strokeWidth={2} />
                </>
              ) : (
                <>
                  {isEntitlementLoading
                    ? "Checking access..."
                    : isPaying
                      ? "Opening checkout..."
                      : "Buy Now"}
                  <ArrowRight
                    className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                    strokeWidth={2}
                  />
                </>
              )}
            </button>

            {hasActiveEntitlement && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/30 bg-[#C5A059]/10 px-4 py-3 text-sm font-medium text-[#E3C985]">
                <CheckCircle2 className="h-4 w-4" />
                Access granted
              </span>
            )}
          </div>

          {/* Stats row */}
          <div className="mt-16 flex flex-wrap items-center gap-8 md:gap-14 fade-up-delay-2">
            <div>
              <div className="font-heading text-2xl md:text-3xl text-white font-semibold">
                4+ <span className="text-[#C5A059] text-base font-light">hrs</span>
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">
                Studio sessions
              </div>
            </div>
            {/* <div className="w-px h-10 bg-white/10 hidden sm:block" /> */}
            {/* <div>
              <div className="font-heading text-2xl md:text-3xl text-white font-semibold">
                30+
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">
                Multitrack files
              </div>
            </div> */}
            <div className="w-px h-10 bg-white/10 hidden sm:block" />
            <div>
              <div className="font-heading text-2xl md:text-3xl text-white font-semibold">
                4.9
                <span className="text-[#C5A059] text-base font-light">/5</span>
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">
                Student rating
              </div>
            </div>
          </div>
        </div>

          <div className="fade-up-delay-2 lg:mb-2">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C5A059]">
                Course Preview
              </p>
              <h2 className="mt-2 font-heading text-2xl font-semibold text-white md:text-3xl">
                A quick message from the course creator.
              </h2>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-[1.35rem] border border-white/10 bg-[#09090B]/85 p-2 shadow-[0_32px_90px_-28px_rgba(0,0,0,0.9),0_0_70px_-44px_rgba(197,160,89,0.9)] backdrop-blur-xl">
            <div className="pointer-events-none absolute inset-0 rounded-[1.35rem] ring-1 ring-inset ring-white/10" />
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-black">
              <iframe
                data-testid="hero-preview-video"
                src={previewVideo.embedUrl}
                title={previewVideo.title}
                className="h-full w-full"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};
